import { css } from "@emotion/css";
import { Callout, RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { fontSize, space, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ActionProperties, ProcessTemplateListSummaryResource, ProcessTemplateVersionSummary } from "@octopusdeploy/octopus-server-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import * as React from "react";
import semver from "semver/preload";
import { getProcessTemplateSlugFromActionProperties, getProcessTemplateVersionMaskFromActionProperties } from "~/areas/projects/components/Process/Blueprints/processTemplateId";
import type { ProcessTemplateDetails } from "~/areas/projects/components/Process/Contexts/ProcessController";
import { ExpandableFormSection, FormSection, FormSectionHeading, Select, Summary } from "~/components/form/index";
interface ProcessTemplateVersionSelectorProps {
    initialActionProperties: ActionProperties;
    initialProcessTemplateVersion: string | undefined | null;
    actionProperties: ActionProperties;
    setVersionMaskProperty: (versionMask: string) => void;
    setProcessTemplateSlugProperty: (slug: string) => void;
    isNew: boolean;
    selectedProcessTemplateDetails: ProcessTemplateDetails | undefined;
    distinctTemplates: ProcessTemplateListSummaryResource[];
    latestProcessTemplateVersionSummary: ProcessTemplateVersionSummary | undefined | null;
    selectedProcessTemplateOption: string | undefined;
    setSelectedProcessTemplateOption: (slug: string) => void;
    selectedUsageConstraint: UsageConstraint;
    setSelectedUsageConstraint: React.Dispatch<React.SetStateAction<UsageConstraint>>;
    selectedUpdateBehaviour: UpdateBehaviour;
    setSelectedUpdateBehaviour: React.Dispatch<React.SetStateAction<UpdateBehaviour>>;
}
export function ProcessTemplateVersionSelector({ initialActionProperties, initialProcessTemplateVersion, actionProperties, setVersionMaskProperty, setProcessTemplateSlugProperty, isNew, selectedProcessTemplateDetails, distinctTemplates, latestProcessTemplateVersionSummary, selectedProcessTemplateOption, setSelectedProcessTemplateOption, selectedUsageConstraint, setSelectedUsageConstraint, selectedUpdateBehaviour, setSelectedUpdateBehaviour, }: ProcessTemplateVersionSelectorProps) {
    const selectedSlug = getProcessTemplateSlugFromActionProperties(actionProperties);
    const initialVersionMask = getProcessTemplateVersionMaskFromActionProperties(initialActionProperties);
    const initialUpdateBehavior = mapMaskToUpdateBehaviour(initialVersionMask);
    const currentVersionMask = getProcessTemplateVersionMaskFromActionProperties(actionProperties);
    const title = selectedSlug ? "Change process template" : "Select process template";
    const summary = selectedSlug ? Summary.placeholder("Change process template by selecting one from the list") : null;
    const distinctProcessTemplateOptions = mapProcessTemplatesToProcessTemplateOptions(distinctTemplates);
    const templateVersionIsAvailable = !!latestProcessTemplateVersionSummary?.LatestStableVersion || !!latestProcessTemplateVersionSummary?.LatestPreReleaseVersion;
    const currentSelectedTemplateName = distinctTemplates.find((v) => v.Slug === selectedProcessTemplateOption)?.Name;
    const isProcessTemplateSelected = !!selectedProcessTemplateOption && (selectedUsageConstraint === UsageConstraint.PreRelease || !!selectedUpdateBehaviour);
    const isProcessTemplateUnavailable = isProcessTemplateSelected && !selectedProcessTemplateDetails && !templateVersionIsAvailable;
    const onUpdateBehaviourChanged = (value: UpdateBehaviour) => {
        setSelectedUpdateBehaviour(value);
        const versionMask = getUpdatedVersionMask(latestProcessTemplateVersionSummary?.LatestStableVersion, initialProcessTemplateVersion, value, initialUpdateBehavior);
        setVersionMaskProperty(versionMask);
    };
    const onUsageConstraintChanged = (value: UsageConstraint) => {
        if (latestProcessTemplateVersionSummary?.IsOnlyPreRelease || value === UsageConstraint.PreRelease) {
            setVersionMaskProperty(UpdateBehaviour.PreRelease);
            setSelectedUsageConstraint(UsageConstraint.PreRelease);
        }
        else {
            setSelectedUsageConstraint(value);
            onUpdateBehaviourChanged(UpdateBehaviour.All);
        }
    };
    const onProcessTemplateChanged = (value: string | undefined) => {
        if (value) {
            setSelectedProcessTemplateOption(value);
            setProcessTemplateSlugProperty(value);
            onUsageConstraintChanged(UsageConstraint.Stable);
        }
    };
    if (latestProcessTemplateVersionSummary && currentVersionMask === "" && selectedUpdateBehaviour === UpdateBehaviour.All) {
        const mappedVersionMask = getVersionMask(latestProcessTemplateVersionSummary.LatestStableVersion, selectedUpdateBehaviour);
        setVersionMaskProperty(mappedVersionMask);
    }
    if (latestProcessTemplateVersionSummary?.IsOnlyPreRelease && currentVersionMask !== UpdateBehaviour.PreRelease && selectedUpdateBehaviour !== UpdateBehaviour.PreRelease) {
        setVersionMaskProperty(UpdateBehaviour.PreRelease);
        setSelectedUsageConstraint(UsageConstraint.PreRelease);
        setSelectedUpdateBehaviour(UpdateBehaviour.PreRelease);
    }
    if (latestProcessTemplateVersionSummary && !latestProcessTemplateVersionSummary?.LatestPreReleaseVersion && currentVersionMask === UpdateBehaviour.PreRelease && selectedUsageConstraint !== UsageConstraint.Stable) {
        setVersionMaskProperty("X");
        setSelectedUsageConstraint(UsageConstraint.Stable);
        setSelectedUpdateBehaviour(UpdateBehaviour.All);
    }
    return (<>
            <ExpandableFormSection isExpandedByDefault={isNew || isProcessTemplateUnavailable} errorKey="selectedProcessTemplate" title={title} summary={summary}>
                <Select value={selectedProcessTemplateOption} allowClear={false} onChange={onProcessTemplateChanged} items={distinctProcessTemplateOptions} label="Select process template" placeholder={"Select Process Template"}/>
                {isProcessTemplateUnavailable && (<>
                        <ProcessTemplateNotFoundCallout />
                    </>)}
            </ExpandableFormSection>
            {selectedProcessTemplateOption && templateVersionIsAvailable && (<>
                    <FormSection title={"Template Name"} includeBorder={true} help={<div>{currentSelectedTemplateName}</div>}></FormSection>

                    <FormSectionHeading title="Versioning"/>
                    {latestProcessTemplateVersionSummary && (<>
                            <OnlyPreReleaseCallout isOnlyPreRelease={latestProcessTemplateVersionSummary.IsOnlyPreRelease}/>

                            <ExpandableFormSection title={"Template Version"} summary={Summary.placeholder(selectedProcessTemplateDetails?.processTemplateVersion ? `${selectedProcessTemplateDetails?.processTemplateVersion}` : null)} help={selectedProcessTemplateDetails?.processTemplateVersion ? <span className={styles.versionHelp}>Version {selectedProcessTemplateDetails?.processTemplateVersion}</span> : null} errorKey={""} isExpandedByDefault={isNew || latestProcessTemplateVersionSummary.IsOnlyPreRelease}>
                                {latestProcessTemplateVersionSummary.LatestPreReleaseVersion && (<>
                                        <RadioButtonGroup value={selectedUsageConstraint} onChange={onUsageConstraintChanged} title={"Select usage constraint"} accessibleName={"Usage constraint"}>
                                            <RadioButton label="Stable release" value={UsageConstraint.Stable} disabled={latestProcessTemplateVersionSummary.IsOnlyPreRelease}/>
                                            <RadioButton label="Pre-release" value={UsageConstraint.PreRelease}/>
                                            <span className={styles.helperText}>
                                                Current pre-release version: {latestProcessTemplateVersionSummary.LatestPreReleaseVersion}. Pre-releases are intended for testing purposes, not regular deployments. Proceed with caution.
                                            </span>
                                        </RadioButtonGroup>
                                    </>)}
                                {selectedUsageConstraint === UsageConstraint.Stable && !latestProcessTemplateVersionSummary.IsOnlyPreRelease && (<RadioButtonGroup value={selectedUpdateBehaviour} onChange={onUpdateBehaviourChanged} title={"Select update behaviour"} accessibleName={"Update behaviour"}>
                                        <RadioButton label="Accept all changes" value={UpdateBehaviour.All}/>
                                        <span className={styles.helperText}>Automatically update the template whenever a patch, minor or major version is published.</span>
                                        <RadioButton label="Accept minor changes" value={UpdateBehaviour.Minor}/>
                                        <span className={styles.helperText}>Automatically update the template whenever a patch or minor version is published.</span>
                                        <RadioButton label="Accept patches" value={UpdateBehaviour.Patch}/>
                                        <span className={styles.helperText}>Automatically update the template whenever a patch is published.</span>
                                    </RadioButtonGroup>)}
                            </ExpandableFormSection>
                        </>)}
                </>)}
        </>);
}
export enum UsageConstraint {
    Stable = "Stable",
    PreRelease = "PreRelease"
}
export enum UpdateBehaviour {
    All = "All",
    Minor = "Minor",//includes Minor and Patch updates
    Patch = "Patch",//patch updates only
    PreRelease = "PreRelease"
}
interface Option {
    value: string;
    text: string;
}
function mapProcessTemplatesToProcessTemplateOptions(processTemplates: ProcessTemplateListSummaryResource[]): Option[] {
    return processTemplates.map((pt) => ({ value: pt.Slug, text: pt.Name }));
}
export function mapMaskToUsageConstraint(value: string): UsageConstraint {
    if (value.toLowerCase() === "prerelease") {
        return UsageConstraint.PreRelease;
    }
    return UsageConstraint.Stable;
}
interface OnlyPreReleaseCalloutProps {
    isOnlyPreRelease: boolean;
}
export function mapMaskToUpdateBehaviour(mask: string): UpdateBehaviour {
    switch (true) {
        case mask.toLowerCase() === "prerelease":
            return UpdateBehaviour.PreRelease;
        case mask === "":
            return UpdateBehaviour.All;
        case /^X$/i.test(mask):
            return UpdateBehaviour.All;
        case /^\d+\.X$/i.test(mask):
            return UpdateBehaviour.Minor;
        case /^\d+\.\d+\.X$/i.test(mask):
            return UpdateBehaviour.Patch;
        default:
            throw new Error(`Invalid version mask: ${mask}`);
    }
}
export function getVersionMask(version: string | undefined | null, updateBehaviour: UpdateBehaviour): string {
    if (!version)
        return "X";
    const major = semver.major(version);
    const minor = semver.minor(version);
    switch (updateBehaviour) {
        case UpdateBehaviour.All:
            return "X";
        case UpdateBehaviour.Minor:
            return `${major}.X`;
        case UpdateBehaviour.Patch:
            return `${major}.${minor}.X`;
        case UpdateBehaviour.PreRelease:
            return "PreRelease";
        default:
            exhaustiveCheck(updateBehaviour, "Invalid UpdateBehaviour");
    }
}
function getUpdatedVersionMask(latestVersion: string | undefined | null, originalVersion: string | undefined | null, updateBehaviour: UpdateBehaviour, originalUpdateBehaviour: UpdateBehaviour): string {
    if (updateBehaviour === originalUpdateBehaviour)
        return getVersionMask(originalVersion, updateBehaviour);
    if (!originalVersion)
        return getVersionMask(latestVersion, updateBehaviour);
    const isMinorToPatchOrViceVersa = (originalUpdateBehaviour === UpdateBehaviour.Minor && updateBehaviour === UpdateBehaviour.Patch) || (originalUpdateBehaviour === UpdateBehaviour.Patch && updateBehaviour === UpdateBehaviour.Minor);
    if (isMinorToPatchOrViceVersa) {
        return getVersionMask(originalVersion, updateBehaviour);
    }
    else {
        return getVersionMask(latestVersion, updateBehaviour);
    }
}
function OnlyPreReleaseCallout({ isOnlyPreRelease }: OnlyPreReleaseCalloutProps) {
    if (isOnlyPreRelease) {
        return (<Callout type={"information"} title={"Only pre-release versions of this template are available."}>
                To use this template, select Pre-release.
            </Callout>);
    }
    return <></>;
}
function ProcessTemplateNotFoundCallout() {
    return (<Callout type={"danger"} title={"This process template is no longer available."}>
            <div> Please select another template.</div>
        </Callout>);
}
const styles = {
    helperText: css({ fontSize: fontSize.xSmall, color: themeTokens.color.text.secondary, marginBottom: space[12] }),
    versionHelp: css({ color: themeTokens.color.text.secondary }),
};
